import type { Vec2 } from "./types.js";

export interface SpatialItem {
  readonly id: string;
  readonly position: Vec2;
}

export class SpatialHash<T extends SpatialItem> {
  private readonly cells = new Map<string, T[]>();

  public constructor(public readonly cellSize: number) {
    if (!Number.isFinite(cellSize) || cellSize <= 0)
      throw new RangeError("cellSize must be positive.");
  }

  public rebuild(items: Iterable<T>): void {
    this.cells.clear();
    for (const item of items) {
      const key = this.key(item.position.x, item.position.y);
      const cell = this.cells.get(key);
      if (cell === undefined) this.cells.set(key, [item]);
      else cell.push(item);
    }
  }

  public queryRadius(position: Vec2, radius: number): readonly T[] {
    const minimumX = Math.floor((position.x - radius) / this.cellSize);
    const maximumX = Math.floor((position.x + radius) / this.cellSize);
    const minimumY = Math.floor((position.y - radius) / this.cellSize);
    const maximumY = Math.floor((position.y + radius) / this.cellSize);
    const radiusSquared = radius * radius;
    const results: T[] = [];
    for (let x = minimumX; x <= maximumX; x += 1) {
      for (let y = minimumY; y <= maximumY; y += 1) {
        for (const item of this.cells.get(`${String(x)}:${String(y)}`) ?? []) {
          const dx = item.position.x - position.x;
          const dy = item.position.y - position.y;
          if (dx * dx + dy * dy <= radiusSquared) results.push(item);
        }
      }
    }
    return results;
  }

  private key(x: number, y: number): string {
    return `${String(Math.floor(x / this.cellSize))}:${String(Math.floor(y / this.cellSize))}`;
  }
}
